import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useRequiredFields, AVAILABLE_FIELDS, FieldPreset } from '@/hooks/useRequiredFields';
import { Plus, CheckCircle2, Trash2, Settings2, Edit2, Info, Check } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';

export const PresetManager = () => {
  const { toast } = useToast();
  const { presets, activePreset, enableFieldPresets, createPreset, updatePreset, deletePreset, activatePreset, toggleFieldPresets, isLoading } = useRequiredFields();
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<FieldPreset | null>(null);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<{id: string, name: string} | null>(null);

  const handleCreatePreset = async () => {
    if (!newPresetName.trim()) {
      toast({ title: 'Error', description: 'Please enter a preset name', variant: 'destructive' });
      return;
    }

    if (selectedFields.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one field', variant: 'destructive' });
      return;
    }

    const { success } = await createPreset(newPresetName, newPresetDescription, selectedFields);
    
    if (success) {
      toast({ title: 'Success', description: `Preset "${newPresetName}" created` });
      setIsCreateDialogOpen(false);
      resetForm();
    } else {
      toast({ title: 'Error', description: 'Failed to create preset', variant: 'destructive' });
    }
  };

  const handleActivatePreset = async (presetId: string) => {
    const success = await activatePreset(presetId);
    if (success) {
      const preset = presets.find(p => p.id === presetId);
      toast({
        title: 'Preset activated',
        description: `"${preset?.name}" is now your active field preset`,
      });
    }
  };

  const startEdit = (preset: FieldPreset) => {
    setEditingPreset(preset);
    setNewPresetName(preset.name);
    setNewPresetDescription(preset.description || '');
    setSelectedFields(preset.fields);
    setIsEditDialogOpen(true);
  };

  const handleUpdatePreset = async () => {
    if (!editingPreset || !newPresetName.trim()) {
      toast({ title: 'Error', description: 'Please enter a preset name', variant: 'destructive' });
      return;
    }

    if (selectedFields.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one field', variant: 'destructive' });
      return;
    }

    const success = await updatePreset(editingPreset.id, {
      name: newPresetName,
      description: newPresetDescription,
      fields: selectedFields,
    });
    
    if (success) {
      toast({ title: 'Success', description: `Preset "${newPresetName}" updated` });
      setIsEditDialogOpen(false);
      resetForm();
      setEditingPreset(null);
    } else {
      toast({ title: 'Error', description: 'Failed to update preset', variant: 'destructive' });
    }
  };

  const handleDeletePreset = async (presetId: string, presetName: string) => {
    setPresetToDelete({ id: presetId, name: presetName });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!presetToDelete) return;
    
    const success = await deletePreset(presetToDelete.id);
    if (success) {
      toast({ title: 'Preset deleted', description: `"${presetToDelete.name}" has been removed` });
    }
    setShowDeleteDialog(false);
    setPresetToDelete(null);
  };

  const resetForm = () => {
    setNewPresetName('');
    setNewPresetDescription('');
    setSelectedFields([]);
  };

  const toggleField = (fieldValue: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldValue)
        ? prev.filter(f => f !== fieldValue)
        : [...prev, fieldValue]
    );
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-5">
      {/* Main toggle card */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border/60 flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 flex items-center justify-center shrink-0">
            <Settings2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Field presets</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Control which contact fields are required in the Add-In. Use presets to focus on what matters for your workflow.
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="enable-field-presets" className="text-sm font-medium">Enable field presets</Label>
              <p className="text-xs text-muted-foreground">Show only the fields you've selected. When disabled, all fields appear.</p>
            </div>
            <Switch
              id="enable-field-presets"
              checked={enableFieldPresets}
              onCheckedChange={async (checked) => {
                const success = await toggleFieldPresets(checked);
                if (success) {
                  toast({
                    title: checked ? 'Field presets enabled' : 'Field presets disabled',
                    description: checked
                      ? 'Only selected fields will be shown in the Add-In'
                      : 'All fields will be shown in the Add-In',
                  });
                }
              }}
            />
          </div>

          {!enableFieldPresets && (
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 flex items-start gap-2.5">
              <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                All contact fields are currently visible in the Add-In.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Preset Management Section - only when enabled */}
      {enableFieldPresets && (
        <>
          {/* Header Card with Active Preset Info */}
          <Card className="app-tile">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Manage Presets</CardTitle>
                  <CardDescription className="text-xs">
                    Create and manage custom field presets
                  </CardDescription>
                </div>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      New Preset
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Preset</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="presetName">Preset Name *</Label>
                        <Input
                          id="presetName"
                          placeholder="e.g. Sales Basic, Full Profile"
                          value={newPresetName}
                          onChange={(e) => setNewPresetName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="presetDescription">Description</Label>
                        <Textarea
                          id="presetDescription"
                          placeholder="Brief description of this preset"
                          value={newPresetDescription}
                          onChange={(e) => setNewPresetDescription(e.target.value)}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(AVAILABLE_FIELDS).map(([category, fields]) => {
                          const categoryIcons: Record<string, string> = {
                            personal: '👤',
                            business: '💼',
                            contact: '📞',
                            address: '📍'
                          };
                          
                          return (
                            <div
                              key={category}
                              className="relative rounded-xl border border-gray-200/50 dark:border-gray-700/50 
                                         bg-white/40 dark:bg-slate-800/40 backdrop-blur-md
                                         p-4 shadow-sm hover:shadow-md transition-all"
                            >
                              <h3 className="text-sm font-semibold mb-3 capitalize flex items-center gap-2">
                                {categoryIcons[category] || '📋'}
                                {category}
                              </h3>
                              <div className="space-y-2">
                                {fields.map(field => (
                                  <div key={field.value} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`new-${field.value}`}
                                      checked={selectedFields.includes(field.value)}
                                      onCheckedChange={() => toggleField(field.value)}
                                      className="border-gray-300 dark:border-gray-600"
                                    />
                                    <Label 
                                      htmlFor={`new-${field.value}`} 
                                      className="text-xs cursor-pointer leading-tight font-normal"
                                    >
                                      {field.label}
                                    </Label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleCreatePreset}>
                          Create Preset
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {activePreset && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-semibold text-sm truncate max-w-[200px]">
                          {activePreset.name}
                        </span>
                        <Badge variant="secondary" className="text-xs ml-auto">
                          {activePreset.fields.length} fields
                        </Badge>
                      </div>
                      {activePreset.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2 ml-6">
                          {activePreset.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preset List */}
          <div className="grid gap-3">
            {presets.map(preset => (
              <div
                key={preset.id}
                className={`relative rounded-xl border p-3 space-y-2 transition-all bg-card/60 backdrop-blur-md
                            ${preset.is_active ? 'border-success/50 bg-success/5' : 'border-border/60 hover:border-border'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold">{preset.name}</h4>
                      {preset.is_active && (
                        <Badge variant="default" className="text-[10px] h-4 py-0">
                          Active
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] h-4 py-0">
                        {preset.fields.length} fields
                      </Badge>
                    </div>
                    {preset.description && (
                      <p className="text-xs text-muted-foreground">{preset.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {preset.fields.slice(0, 5).map(field => (
                        <Badge key={field} variant="secondary" className="text-[10px] h-4 py-0">
                          {field}
                        </Badge>
                      ))}
                      {preset.fields.length > 5 && (
                        <Badge variant="secondary" className="text-[10px] h-4 py-0">
                          +{preset.fields.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-1.5">
                  {!preset.is_active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleActivatePreset(preset.id)}
                      className="h-7 text-xs"
                    >
                      Activate
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(preset)}
                    className="h-7 text-xs"
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeletePreset(preset.id, preset.name)}
                    className="h-7 text-xs"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}

            {presets.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Settings2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">
                    No presets yet. Create your first preset to get started.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editPresetName">Preset Name *</Label>
              <Input
                id="editPresetName"
                placeholder="e.g. Sales Basic, Full Profile"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="editPresetDescription">Description</Label>
              <Textarea
                id="editPresetDescription"
                placeholder="Brief description of this preset"
                value={newPresetDescription}
                onChange={(e) => setNewPresetDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(AVAILABLE_FIELDS).map(([category, fields]) => {
                const categoryIcons: Record<string, string> = {
                  personal: '👤',
                  business: '💼',
                  contact: '📞',
                  address: '📍'
                };
                
                return (
                  <div
                    key={category}
                    className="relative rounded-xl border border-gray-200/50 dark:border-gray-700/50 
                               bg-white/40 dark:bg-slate-800/40 backdrop-blur-md
                               p-4 shadow-sm hover:shadow-md transition-all"
                  >
                    <h3 className="text-sm font-semibold mb-3 capitalize flex items-center gap-2">
                      {categoryIcons[category] || '📋'}
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {fields.map(field => (
                        <div key={field.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-${field.value}`}
                            checked={selectedFields.includes(field.value)}
                            onCheckedChange={() => toggleField(field.value)}
                            className="border-gray-300 dark:border-gray-600"
                          />
                          <Label 
                            htmlFor={`edit-${field.value}`} 
                            className="text-xs cursor-pointer leading-tight font-normal"
                          >
                            {field.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
                setEditingPreset(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePreset}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the preset "{presetToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setPresetToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};